import { lightenColor } from "../../../../utils/colors.ts"
import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { readAllDayDate } from "../../../../utils/date/get-date-safe.ts"
import { type CalendarEvent, type Event } from "../../types/types.ts"
import { fromUTC } from "../components/CalendarAddForms/time.ts"
import { NEUTRAL_EVENT_COLORS } from "../constants/event-colors.ts"
import { getStoredEventColor } from "./event-custom-colors.ts"

// A reminder carries no end, and FullCalendar would otherwise block out its
// default hour for it.
const OPEN_ENDED_MINUTES = 15

export const transformEventsForCalendar = (
  events: Event[],
  // `fromUTC` falls back to dayjs's *global* default zone (DayjsProvider.tsx
  // — the account's own timezone) when none is passed, which stopped being
  // correct once Calendar Settings could give the Calendar page its own,
  // different *effective* timezone: FullCalendar's `timeZone` prop treats an
  // event's wall-clock digits as already being in that zone (it discards any
  // offset the string carries, once `timeZone` is a named zone rather than
  // "local"/"UTC"), so a mismatch here — not passing the same effective
  // timezone the grid itself renders with — shows the *raw account-zone*
  // wall-clock time as if it were already the override zone, off by whatever
  // the two zones' difference is. Explicit, not implicit, so this can never
  // silently drift from what `<FullCalendar timeZone=...>` is actually using.
  timezone: string
): CalendarEvent[] => {
  if (!events || !Array.isArray(events)) return []

  return events.map((event) => {
    const type = event?.extended_props?.type || "event"
    // A per-row fallback gave two identical events different colours purely by
    // their position in the response.
    const theme = NEUTRAL_EVENT_COLORS
    const completed = event?.extended_props?.is_completed || false

    // A user-picked color overrides the server-computed type color entirely
    // — the backend has no writable color field, so this is stored and read
    // client-side only (see event-custom-colors.ts).
    const customColor = getStoredEventColor(event?.id)

    const backgroundColor =
      customColor?.background_color ||
      event?.background_color ||
      theme?.background_color
    const borderColor =
      customColor?.border_color || event?.border_color || theme?.border_color
    const textColor =
      customColor?.text_color || event?.text_color || theme?.text_color

    const isAllDay = event?.all_day || false

    // Was reminder-only ("whether an all-day event renders in FullCalendar's
    // all-day row... is a separate open question", per
    // docs/kb/decisions/2026-08-20-all-day-is-read-through-one-shared-reader.md)
    // until Create/Edit Event gained an All Day toggle of their own.
    const allDay = isAllDay

    const start = isAllDay
      ? readAllDayDate(event?.start_at).format(DATE_FORMAT)
      : fromUTC(event?.start_at, timezone).format()

    let end: string | undefined
    if (event?.end_at) {
      end = isAllDay
        ? readAllDayDate(event.end_at, event.start_at)
            .add(1, "day")
            .format(DATE_FORMAT)
        : fromUTC(event.end_at, timezone).format()
    } else if (!isAllDay) {
      end = fromUTC(event?.start_at, timezone)
        .add(OPEN_ENDED_MINUTES, "minute")
        .format()
    }

    return {
      // id: event?.id,
      originalId: event?.id,
      title: event?.title,
      start,
      end,
      allDay,
      backgroundColor: completed
        ? lightenColor(backgroundColor, 65)
        : backgroundColor,
      borderColor: completed ? lightenColor(borderColor, 65) : borderColor,
      textColor: textColor,
      // Only timed meetings reschedule by drag: a reminder's synthetic display
      // end and an all-day meeting's `allDay: false` are presentation-only and
      // must never be persisted. The key must be absent for a draggable row —
      // even an explicit `undefined` is refined to `false` — so it defers to
      // the calendar-wide `editable` and the permission gate keeps working.
      ...((type !== "event" || isAllDay) && { startEditable: false }),

      extendedProps: {
        ...event,
        ...event?.extended_props,
        completed: event?.is_completed || false
      }
    } as any
  })
}
